import { useState, useEffect, useCallback } from "react"
import { useAuth } from '../../common/context/AuthContext'
import { useNavigate, useLocation } from "react-router-dom"
import {
Search,Plus,Minus, ShoppingBag, AlertTriangle, X, Check, Coffee, ChevronRight, Clock, DollarSign, Utensils, ShoppingCart, BookOpen,
Info, Trash2, Loader2, CheckCircle2, StickyNote, Tag, Save, Printer, ChefHat, CreditCard, Smartphone, FileText, Percent} from "lucide-react"
import { API_URL } from '../../api/config'

const MenuSelection = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { tableId, existingHoldBillId, existingItems, isAddingItems } = location.state || {}
  const [selectedItems, setSelectedItems] = useState([])
  const { userToken: token, restaurantId } = useAuth()
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(false)
  const [savingBill, setSavingBill] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [categoryFilter] = useState("all")
  const [showVariantModal, setShowVariantModal] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [tableHoldBills, setTableHoldBills] = useState([])
  const [showHoldBillsModal, setShowHoldBillsModal] = useState(false)
  const [customAlertVisible, setCustomAlertVisible] = useState(false)
  const [orderType, setOrderType] = useState('dining')
  const [customAlertOptions, setCustomAlertOptions] = useState({
    title: "",
    message: "",
    buttons: [],
  })
  const [isCartMobileOpen, setIsCartMobileOpen] = useState(false)
  const [editingNoteId, setEditingNoteId] = useState(null)
  const [editingVariantId, setEditingVariantId] = useState(null)
  const [showCustomerDetails, setShowCustomerDetails] = useState(false) 
  const [tableDetails, setTableDetails] = useState(null)
  const [loadingTable, setLoadingTable] = useState(false)
  const [discountType, setDiscountType] = useState('percentage')
  const [discountPercentage, setDiscountPercentage] = useState(0)
  const [discountAmount, setDiscountAmount] = useState(0)
  const [paymentMode, setPaymentMode] = useState('Cash')
  const [customerName, setCustomerName] = useState('')
  const [customerMobile, setCustomerMobile] = useState('')
  const [sendingKOT, setSendingKOT] = useState(false)
  const [toasts, setToasts] = useState([])
  const [activeTab, setActiveTab] = useState("all")

  const addToast = useCallback((message, type = "info") => {
    const id = Date.now()
    setToasts((prev) => [...prev, { id, message, type }])
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, 3000)
  }, [])

  const fetchTableDetails = useCallback(async () => {
    if (!tableId || !token) return
    
    setLoadingTable(true)
    try {
      const response = await fetch(`${API_URL}/tables`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })
      
      if (!response.ok) throw new Error('Failed to fetch tables')
      
      const result = await response.json()
      const tablesData = Array.isArray(result) ? result : (result.data || [])
      
      const table = tablesData.find(t => t.id === tableId || t._id === tableId)
      
      if (table) {
        setTableDetails(table)
      } else {
        console.warn('Table not found in list')
        setTableDetails({ 
          tableName: `Table ${String(tableId).substring(0, 6)}`,
          tableNumber: null 
        })
      }
    } catch (error) {
      console.error('Error fetching table details:', error)
      setTableDetails({ 
        tableName: `Table ${String(tableId).substring(0, 6)}`,
        tableNumber: null 
      })
    } finally {
      setLoadingTable(false)
    }
  }, [tableId, token])

  const fetchTableHoldBills = useCallback(async () => {
    try {
      setLoading(true)
      const response = await fetch(`${API_URL}/hold-bills/table/${tableId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      
      if (!response.ok) {
        throw new Error("Failed to fetch hold bills for the table")
      }
      
      const result = await response.json()
      const billsData = Array.isArray(result) ? result : (result.data || [])
      
      if (billsData.length > 0) {
        setTableHoldBills(billsData)
        setShowHoldBillsModal(true)
      }
    } catch (error) {
      console.error("Error fetching table hold bills:", error)
      addToast("Error: Failed to load existing hold bills", "error")
    } finally {
      setLoading(false)
    }
  }, [tableId, token, addToast])

  useEffect(() => {
    if (tableId) {
      fetchTableDetails()
    }
  }, [fetchTableDetails, tableId])

  useEffect(() => {
    if (isAddingItems && existingItems) {
      const transformedItems = existingItems.map((item) => ({
        id: item.productId || item.id || item._id,
        _id: item.productId || item.id || item._id,
        item_name: item.item_name,
        quantity: item.quantity || 1,
        price: item.price,
        pricingType: item.pricingType || 'basePrice',
        category: item.category,
        selectedVariant: item.variant,
        names: item.names || "",
        note: item.note || "",
        originalItem: item,
      }))
      setSelectedItems(transformedItems)
    } else if (tableId && !isAddingItems && !existingHoldBillId) {
      fetchTableHoldBills()
    }
  }, [isAddingItems, existingItems, tableId, existingHoldBillId, fetchTableHoldBills])

  const handleProceed = useCallback(async () => {
    if (selectedItems.length === 0) {
      addToast("Error: Please select at least one item", "error")
      return
    }

    if (isAddingItems && existingHoldBillId) {
      setLoading(true)
      try {
        const formattedItems = selectedItems.map((item) => {
          const productId = item.id || item._id || item.productId

          return {
            productId: productId,
            _id: productId,
            item_name: item.item_name,
            quantity: item.quantity || 1,
            category: item.category || "Veg",
            pricingType: item.pricingType || 'basePrice',
            price: item.selectedVariant ? item.selectedVariant.price : item.price,
            selectedVariant: item.selectedVariant,
            variant: item.selectedVariant,
            names: item.names || "",
            note: item.note || "",
          }
        })

        const updateResponse = await fetch(`${API_URL}/hold-bills/${existingHoldBillId}`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            items: formattedItems,
            action: "replace",
          }),
        })

        const responseData = await updateResponse.json()

        if (!updateResponse.ok) {
          if (responseData.invalidProducts && responseData.invalidProducts.length > 0) {
            const invalidItems = responseData.invalidProducts
              .map((p) => `${p.productName || p.productId}: ${p.message}`)
              .join("\n")

            throw new Error(`Invalid items found:\n${invalidItems}`)
          }
          throw new Error(responseData.message || "Failed to update hold bill")
        }

        addToast("Success: Hold bill updated successfully", "success")
        navigate("/new-bill")
      } catch (error) {
        console.error("Error updating hold bill:", error)
        addToast(`Update Failed: ${error.message}`, "error")
      } finally {
        setLoading(false)
      }
    } else {
      navigate("/bill-details", {
        state: { selectedItems, tableId, isHoldBill: true },
      })
    }
  }, [selectedItems, isAddingItems, existingHoldBillId, token, addToast, navigate, tableId])

 const handleDirectHold = useCallback(async () => {
  if (selectedItems.length === 0) {
    addToast("Error: Please select at least one item", "error")
    return
  }
  
  if (customerMobile && !/^[6-9]\d{9}$/.test(customerMobile)) {
    addToast("Please enter a valid 10-digit mobile number", "error")
    return
  }
  
  let basePriceSubtotal = 0
  let mrpSubtotal = 0
  
  selectedItems.forEach(item => {
    const price = item.selectedVariant ? item.selectedVariant.price : item.price
    const itemTotal = price * (item.quantity || 1)
    
    if (item.pricingType === 'basePrice') {
      basePriceSubtotal += itemTotal
    } else if (item.pricingType === 'mrp') {
      mrpSubtotal += itemTotal
    } else {
      basePriceSubtotal += itemTotal
    }
  })
  
  const subtotal = basePriceSubtotal + mrpSubtotal
  
  if (discountType === 'percentage' && (discountPercentage < 0 || discountPercentage > 100)) {
    addToast("Discount percentage must be between 0 and 100", "error")
    return
  }
  if (discountType === 'amount' && discountAmount > subtotal) {
    addToast(`Discount amount cannot exceed subtotal (₹${subtotal})`, "error")
    return
  }
  
  console.log('=== SENDING HOLD BILL ===');
  console.log('Selected Items:');
  selectedItems.forEach(item => {
    console.log(`  ${item.item_name}: pricingType = ${item.pricingType}, price = ${item.price}`);
  });
  
  setLoading(true)
  try {
    const requestBody = {
      tableId,
      items: selectedItems.map((item) => ({
        productId: item.id || item._id,
        quantity: item.quantity,
        variant: item.selectedVariant,
        names: item.names || "",
        note: item.note || "",
        pricingType: item.pricingType || 'basePrice'
      })),
      names: customerName.trim(),
      mobileNumber: customerMobile.trim(),
      discountType,
      discountPercentage: discountType === 'percentage' ? Number(discountPercentage) : 0,
      discountAmount: discountType === 'amount' ? Number(discountAmount) : 0,
      paymentMode,
    };
    
    console.log('Request body:', JSON.stringify(requestBody, null, 2));
    
    const response = await fetch(`${API_URL}/hold-bills`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    })
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || "Failed to hold bill")
    }
    
    const result = await response.json();
    console.log('Hold bill created:', result);
    
    addToast("Success: Bill placed on hold successfully", "success")
    navigate("/new-bill")
  } catch (error) {
    console.error("Hold bill error:", error)
    addToast(`Error: ${error.message}`, "error")
  } finally {
    setLoading(false)
  }
}, [selectedItems, customerMobile, discountType, discountPercentage, discountAmount, addToast, tableId, customerName, paymentMode, token, navigate])

  const showBackConfirmation = useCallback(() => {
    const isCreatingNewHoldBill = !isAddingItems && !existingHoldBillId
    const isUpdatingHoldBill = isAddingItems && existingHoldBillId
    const hasSelectedItems = selectedItems.length > 0

    setCustomAlertVisible(true)
    setCustomAlertOptions({
      title: "Confirm Action",
      message: hasSelectedItems
        ? "You have selected items. What would you like to do?"
        : "Are you sure you want to go back?",
      buttons: [
        ...(!isCreatingNewHoldBill && hasSelectedItems
          ? [
              {
                text: isAddingItems ? "Update Hold Bill" : "Update",
                onPress: handleProceed,
                style: "primary",
              },
            ]
          : []),
        ...(!isUpdatingHoldBill && hasSelectedItems
          ? [
              {
                text: "Hold Bill",
                onPress: handleDirectHold,
                style: "danger",
              },
            ]
          : []),
        {
          text: "Continue Editing",
          style: "default",
        },
        {
          text: "Cancel",
          onPress: () => navigate(-1),
          style: "cancel",
        },
      ],
    })
  }, [isAddingItems, existingHoldBillId, selectedItems, handleProceed, handleDirectHold, navigate])

  useEffect(() => {
    const handleBackButton = (e) => {
      e.preventDefault()
      showBackConfirmation()
    }
    window.addEventListener("popstate", handleBackButton)
    return () => window.removeEventListener("popstate", handleBackButton)
  }, [showBackConfirmation])

  const CustomAlertModal = () => (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 transition-opacity duration-300 ${customAlertVisible ? "opacity-100" : "opacity-0 pointer-events-none"}`}
    >
      <div className="bg-white rounded-[2rem] w-11/12 max-w-md overflow-hidden shadow-xl transform transition-all">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-800 px-6 py-6">
          <h3 className="text-xl font-black text-white tracking-tight">{customAlertOptions.title}</h3>
        </div>
        <div className="p-6">
          <p className="text-slate-600 mb-6 font-medium">{customAlertOptions.message}</p>
          <div className="flex flex-col gap-3">
            {customAlertOptions.buttons.map((button, index) => (
              <button
                key={index}
                className={`py-3 px-4 rounded-xl font-bold text-center transition-all 
                  ${
                    button.style === "primary"
                      ? "bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-100"
                      : button.style === "danger"
                        ? "bg-red-600 text-white hover:bg-red-700 shadow-lg shadow-red-100"
                        : button.style === "cancel"
                          ? "bg-slate-100 text-slate-700 hover:bg-slate-200"
                          : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                  }`}
                onClick={() => {
                  setCustomAlertVisible(false)
                  button.onPress && button.onPress()
                }}
              >
                {button.text}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )

  const selectExistingHoldBill = (holdBill) => {
    const transformedItems = holdBill.items.map((item) => ({
      id: item.productId || item.id,
      _id: item.productId || item.id,
      item_name: item.item_name,
      quantity: item.quantity,
      price: item.price,
      pricingType: item.pricingType || 'basePrice',
      category: item.category,
      selectedVariant: item.variant,
      names: item.names,
      note: item.note || "",
      originalItem: item,
    }))
    setSelectedItems(transformedItems)
    setShowHoldBillsModal(false)
    navigate("/menu-selection", {
      state: {
        tableId,
        existingHoldBillId: holdBill.id || holdBill._id,
        existingItems: transformedItems,
        isAddingItems: true,
      },
    })
  }

const normalizeProduct = (product) => {
  return {
    ...product,
    id: product.id || product._id,
    _id: product.id || product._id,
    pricingType: product.pricing_type || product.pricingType || 'basePrice',
    itemName: product.item_name || product.itemName,
    item_name: product.item_name || product.itemName,
    basePrice: product.base_price || product.basePrice,
    sellingPrice: product.selling_price || product.sellingPrice,
    imageUrl: product.image_url || product.imageUrl,
    inStock: product.in_stock !== undefined ? product.in_stock : product.inStock,
    unitType: product.unit_type || product.unitType,
  };
};

const fetchProducts = useCallback(async () => {
  setLoading(true)
  try {
    if (!token) {
      throw new Error('Authentication token is missing')
    }
    
    const response = await fetch(`${API_URL}/products`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    })
    
    if (!response.ok) {
      const errorText = await response.text()
      console.error('Products fetch error:', response.status, errorText)
      throw new Error(`Failed to fetch products: ${response.status} ${response.statusText}`)
    }
    
    const result = await response.json()
    
    let productsData = []
    if (Array.isArray(result)) {
      productsData = result
    } else if (result.data && Array.isArray(result.data)) {
      productsData = result.data
    } else if (result.success && result.data && Array.isArray(result.data)) {
      productsData = result.data
    } else {
      console.error('Unexpected response format:', result)
      throw new Error('Invalid response format from server')
    }
    
    const normalizedProducts = productsData.map(normalizeProduct)
    
    const inStockProducts = normalizedProducts.filter((product) => product.inStock !== false)
    
    console.log('=== PRODUCTS LOADED ===');
    inStockProducts.forEach(p => {
      console.log(`${p.item_name}: pricingType = ${p.pricingType}`);
    });
    
    setProducts(inStockProducts)
    
    if (inStockProducts.length === 0) {
      addToast("No products available in menu", "info")
    }
  } catch (error) {
    console.error('Error fetching products:', error)
    addToast(`Error: ${error.message}`, "error")
  } finally {
    setLoading(false)
  }
}, [token, addToast])

  useEffect(() => {
    fetchProducts()
  }, [fetchProducts])

  const filteredProducts = products.filter((product) => {
    const item_name = product.item_name || ""
    const type = product.type || ""
    const category = product.category || ""
    
    const matchesSearch =
      item_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      type.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = categoryFilter === "all" || category === categoryFilter
    const matchesType = activeTab === "all" || type === activeTab
    return matchesSearch && matchesCategory && matchesType
  })

  const groupedProducts = filteredProducts.reduce((acc, product) => {
    const productType = product.type || "Other"
    if (!acc[productType]) {
      acc[productType] = []
    }
    acc[productType].push(product)
    return acc
  }, {})

const determinePricingType = (product) => {
  if (product.pricingType) {
    return product.pricingType;
  }
  
  if (product.pricing_type) {
    return product.pricing_type;
  }
  
  if (product.mrp && product.sellingPrice) {
    return 'mrp';
  }
  
  if (product.basePrice && !product.mrp) {
    return 'basePrice';
  }
  
  const itemName = (product.item_name || product.itemName || '').toLowerCase();
  if (itemName.includes('mrp') || itemName.includes('coke') || itemName.includes('pepsi')) {
    return 'mrp';
  }
  
  return 'basePrice';
};

const handleQuantityChange = (product, change) => {
  setSelectedItems((prevItems) => {
    const productId = product.id || product._id
    
    const existingItemIndex = prevItems.findIndex((item) => {
      const itemId = item.id || item._id
      if (product.selectedVariant) {
        return itemId === productId && item.selectedVariant?.name === product.selectedVariant.name
      }
      return itemId === productId && !item.selectedVariant
    })

    if (existingItemIndex === -1 && change > 0) {
      const pricingType = product.pricingType || 'basePrice';
      
      console.log(`Adding item: ${product.item_name}`);
      console.log(`  - pricingType: ${pricingType}`);
      
      const newItem = {
        id: productId,
        _id: productId,
        ...product,
        quantity: 1,
        note: "",
        price: product.selectedVariant
          ? product.selectedVariant.price
          : pricingType === "mrp"
            ? product.sellingPrice || product.mrp
            : product.basePrice,
        pricingType: pricingType,
        category: product.category || 'Veg'
      }
      
      console.log('New item created:', {
        name: newItem.item_name,
        pricingType: newItem.pricingType,
        price: newItem.price
      });
      
      return [...prevItems, newItem]
    } else if (existingItemIndex !== -1) {
      const updatedItems = [...prevItems]
      const currentItem = updatedItems[existingItemIndex]
      const newQuantity = (currentItem.quantity || 1) + change

      if (newQuantity <= 0) {
        updatedItems.splice(existingItemIndex, 1)
      } else {
        updatedItems[existingItemIndex] = { ...currentItem, quantity: newQuantity }
      }
      return updatedItems
    }
    return prevItems
  })
}

  const getItemQuantity = (product) => {
    const productId = product.id || product._id
    
    if (product.variants && product.variants.length > 0) {
      const variantItems = selectedItems.filter((item) => {
        const itemId = item.id || item._id
        return itemId === productId && item.selectedVariant
      })
      return variantItems.reduce((sum, item) => sum + (item.quantity || 1), 0)
    }

    const item = selectedItems.find((item) => {
      const itemId = item.id || item._id
      return itemId === productId && !item.selectedVariant
    })
    return item ? item.quantity || 1 : 0
  }

  const calculateTotals = () => {
    let basePriceSubtotal = 0
    let mrpSubtotal = 0
    
    console.log('=== CALCULATING TOTALS ===')
    
    selectedItems.forEach(item => {
      const price = item.selectedVariant ? item.selectedVariant.price : item.price
      const itemTotal = price * (item.quantity || 1)
      
      console.log(`Item: ${item.item_name}, PricingType: ${item.pricingType}, Price: ${price}, Qty: ${item.quantity}, Total: ${itemTotal}`)
      
      if (item.pricingType === 'basePrice') {
        basePriceSubtotal += itemTotal
        console.log(`  -> Added to basePriceSubtotal: ${itemTotal}`)
      } else if (item.pricingType === 'mrp') {
        mrpSubtotal += itemTotal
        console.log(`  -> Added to mrpSubtotal: ${itemTotal}`)
      } else {
        console.warn(`  -> WARNING: Unknown pricingType "${item.pricingType}", treating as basePrice`)
        basePriceSubtotal += itemTotal
      }
    })
    
    const subtotal = basePriceSubtotal + mrpSubtotal
    const cgst = basePriceSubtotal * 0.025
    const sgst = basePriceSubtotal * 0.025
    const totalTax = cgst + sgst
    
    console.log(`Base Price Subtotal: ${basePriceSubtotal}`)
    console.log(`MRP Subtotal: ${mrpSubtotal}`)
    console.log(`Total Subtotal: ${subtotal}`)
    console.log(`CGST (2.5% of ${basePriceSubtotal}): ${cgst}`)
    console.log(`SGST (2.5% of ${basePriceSubtotal}): ${sgst}`)
    console.log('======================')
    
    const totalBeforeDiscount = subtotal + totalTax
    
    let discount = 0
    if (discountType === 'percentage' && discountPercentage > 0) {
      discount = (totalBeforeDiscount * discountPercentage) / 100
    } else if (discountType === 'amount' && discountAmount > 0) {
      discount = Math.min(discountAmount, totalBeforeDiscount)
    }
    
    const total = Math.max(0, totalBeforeDiscount - discount)
    
    return {
      subtotal: Number(subtotal.toFixed(2)),
      basePriceSubtotal: Number(basePriceSubtotal.toFixed(2)),
      mrpSubtotal: Number(mrpSubtotal.toFixed(2)),
      cgst: Number(cgst.toFixed(2)),
      sgst: Number(sgst.toFixed(2)),
      totalTax: Number(totalTax.toFixed(2)),
      discount: Number(discount.toFixed(2)),
      total: Number(total.toFixed(2))
    }
  }
  
  const { subtotal, discount, total } = calculateTotals()
  const totalAmount = total

const handleItemPress = (product) => {
  if (product.variants && product.variants.length > 0) {
    setSelectedProduct(product)
    setShowVariantModal(true)
  } else {
    const pricingType = product.pricingType || 'basePrice';
    
    const baseProduct = {
      ...product,
      price: pricingType === "mrp" 
        ? product.sellingPrice || product.mrp 
        : product.basePrice,
      pricingType: pricingType,
      category: product.category || 'Veg'
    }
    handleQuantityChange(baseProduct, 1)
  }
}

const handleSaveAndPrintBill = async () => {
  if (selectedItems.length === 0) {
    addToast("Error: Please select at least one item", "error")
    return
  }
    
  if (customerMobile && !/^[6-9]\d{9}$/.test(customerMobile)) {
    addToast("Please enter a valid 10-digit mobile number", "error")
    return
  }
  
  if (discountType === 'percentage' && (discountPercentage < 0 || discountPercentage > 100)) {
    addToast("Discount percentage must be between 0 and 100", "error")
    return
  }
  if (discountType === 'amount' && discountAmount > subtotal) {
    addToast(`Discount amount cannot exceed subtotal (₹${subtotal})`, "error")
    return
  }

  setSavingBill(true)
  try {
    if (!tableId) throw new Error('Table ID is required')
    if (!restaurantId) throw new Error('Restaurant authentication required')
    if (!token) throw new Error('Authentication token required')

    // CRITICAL FIX: Always use current selectedItems, never fetch from hold bill
    const formattedItems = selectedItems.map((item) => ({
      productId: item.id || item._id || item.productId,
      item_name: item.item_name,
      itemName: item.item_name, // FIXED: Include both formats
      quantity: item.quantity || 1,
      category: item.category || 'Veg',
      pricingType: item.pricingType || 'basePrice',
      price: item.selectedVariant ? item.selectedVariant.price : item.price,
      variant: item.selectedVariant,
      names: item.names || '',
      note: item.note || '',
    }))

    console.log('=== FINALIZE & PRINT DEBUG ===');
    console.log('Using CURRENT selectedItems from state:');
    selectedItems.forEach(item => {
      console.log(`  - ${item.item_name}: qty=${item.quantity}, pricingType=${item.pricingType}`);
    });
    console.log('Formatted items to save:', formattedItems);

    // Calculate totals for the bill data
    const totals = calculateTotals();
    console.log('Calculated totals:', totals);

    let holdBillId = existingHoldBillId
    
    // If editing an existing hold bill, UPDATE it with current items first
    if (holdBillId) {
      console.log(`Updating existing hold bill ${holdBillId} with current items before finalizing`);
      
      const updateResponse = await fetch(`${API_URL}/hold-bills/${holdBillId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          items: formattedItems.map(item => ({
            productId: item.productId,
            quantity: item.quantity,
            variant: item.variant,
            names: item.names || '',
            note: item.note || '',
            pricingType: item.pricingType || 'basePrice'
          })),
          names: customerName.trim(),
          mobileNumber: customerMobile.trim(),
          discountType,
          discountPercentage: discountType === 'percentage' ? Number(discountPercentage) : 0,
          discountAmount: discountType === 'amount' ? Number(discountAmount) : 0,
          paymentMode
        }),
      })

      if (!updateResponse.ok) {
        const updateError = await updateResponse.json().catch(() => ({}))
        throw new Error(updateError.message || 'Failed to update hold bill before finalizing')
      }

      console.log('Hold bill updated successfully with current items');
    } else {
      // Create new hold bill if one doesn't exist
      console.log('Creating new hold bill with current items');
      
      const holdBillResponse = await fetch(`${API_URL}/hold-bills`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          tableId: tableId,
          items: formattedItems.map(item => ({
            productId: item.productId,
            quantity: item.quantity,
            variant: item.variant,
            names: item.names || '',
            note: item.note || '',
            pricingType: item.pricingType || 'basePrice'
          })),
          names: customerName.trim(),
          mobileNumber: customerMobile.trim(),
          discountType,
          discountPercentage: discountType === 'percentage' ? Number(discountPercentage) : 0,
          discountAmount: discountType === 'amount' ? Number(discountAmount) : 0,
          paymentMode
        }),
      })

      if (!holdBillResponse.ok) {
        const holdBillError = await holdBillResponse.json().catch(() => ({}))
        throw new Error(holdBillError.message || 'Failed to create hold bill')
      }

      const holdBillData = await holdBillResponse.json()
      holdBillId = holdBillData.data?.id || holdBillData.data?._id || holdBillData.id || holdBillData._id
      console.log('New hold bill created:', holdBillId);
    }

    // Now save/finalize the bill - the backend will use the updated hold bill items
    console.log('Finalizing bill for table:', tableId);
    
    const saveResponse = await fetch(`${API_URL}/bills/${tableId}/save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        discountPercentage: discountType === 'percentage' ? Number(discountPercentage) : 0,
        discountAmount: discountType === 'amount' ? Number(discountAmount) : 0,
        items: formattedItems,
        names: customerName.trim(),
        mobileNumber: customerMobile.trim(),
        paymentMode
      }),
    })

    if (!saveResponse.ok) {
      const errorText = await saveResponse.text()
      let errorMessage
      try {
        const errorData = JSON.parse(errorText)
        errorMessage = errorData.message || 'Failed to save bill'
        console.error('Save bill error details:', errorData)
      } catch {
        errorMessage = `Server error: ${saveResponse.status} ${saveResponse.statusText}`
        console.error('Save bill error response:', errorText)
      }
      throw new Error(errorMessage)
    }

    const saveData = await saveResponse.json()
    console.log('Save response:', saveData);

    // Delete the hold bill after successful save
    if (holdBillId) {
      try {
        await fetch(`${API_URL}/hold-bills/${holdBillId}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        console.log('Hold bill deleted after finalization');
      } catch (deleteError) {
        console.warn('Failed to delete hold bill:', deleteError)
      }
    }

    addToast("Success: Bill saved successfully", "success")

    const bill = saveData.bill || saveData
    const billId = bill.id || bill._id
    
    if (!billId) {
      console.error('No bill ID found in response:', saveData)
      throw new Error('Failed to get bill ID from server response')
    }

    console.log('Bill finalized successfully:', billId);
    
    // CRITICAL FIX: Pass complete bill data with ALL fields to print page
    const completeBillData = {
      id: billId,
      _id: billId,
      billNumber: bill.billNumber,
      items: formattedItems,
      subtotal: totals.subtotal,
      cgst: totals.cgst,
      sgst: totals.sgst,
      totalAmount: totals.total,
      discountType: discountType,
      discountPercentage: discountType === 'percentage' ? Number(discountPercentage) : 0,
      discountAmount: totals.discount,
      paymentMethod: paymentMode,
      names: customerName.trim(),
      mobileNumber: customerMobile.trim(),
      tableId: tableId,
      tableNumber: tableDetails?.tableNumber || null,
      tableName: tableDetails?.tableName || `Table ${String(tableId).substring(0, 6)}`,
      createdAt: bill.createdAt || new Date().toISOString()
    };

    console.log('Complete bill data being passed:', completeBillData);
    console.log('=== END DEBUG ===');

if (orderType === 'takeaway') {
  navigate(`/print-takeaway-bill/${billId}`, {
    state: {
      billId: billId,
      billData: completeBillData
    },
  })
} else {
  navigate(`/print-bill/${billId}`, {
    state: {
      billId: billId,
      billData: completeBillData
    },
  })
}
  } catch (error) {
    console.error('Error saving bill:', error)
    addToast(`Failed to save bill: ${error.message}`, "error")
  } finally {
    setSavingBill(false)
  }
}

  const handleSendKOT = async () => {
    if (selectedItems.length === 0) {
      addToast("Error: Please select at least one item", "error")
      return
    }

    setSendingKOT(true)
    try {
      if (!tableId) throw new Error('Table ID is required')
      if (!token) throw new Error('Authentication token required')

      let holdBillId = existingHoldBillId
      
      if (!holdBillId) {
        const holdBillResponse = await fetch(`${API_URL}/hold-bills`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            tableId,
            items: selectedItems.map((item) => ({
              productId: item.id || item._id,
              quantity: item.quantity,
              variant: item.selectedVariant,
              names: item.names || '',
              note: item.note || '',
              pricingType: item.pricingType || 'basePrice'
            })),
            names: customerName.trim(),
            mobileNumber: customerMobile.trim(),
            discountType,
            discountPercentage: discountType === 'percentage' ? Number(discountPercentage) : 0,
            discountAmount: discountType === 'amount' ? Number(discountAmount) : 0,
            paymentMode,
          }),
        })

        if (!holdBillResponse.ok) {
          const error = await holdBillResponse.json().catch(() => ({}))
          throw new Error(error.message || 'Failed to create hold bill')
        }

        const holdBillData = await holdBillResponse.json()
        holdBillId = holdBillData.data?.id || holdBillData.data?._id || holdBillData.id || holdBillData._id
      }

      const formattedItems = selectedItems.map((item) => ({
        productId: item.id || item._id || item.productId,
        item_name: item.item_name,
        quantity: item.quantity || 1,
        category: item.category || 'Veg',
        price: item.selectedVariant ? item.selectedVariant.price : item.price,
        variant: item.selectedVariant,
        note: item.note || '',
        pricingType: item.pricingType || 'basePrice'
      }))

      const kotResponse = await fetch(`${API_URL}/kot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          tableId: tableId,
          holdBillId: holdBillId,
          items: formattedItems,
          customerName: customerName.trim() || '',
          timestamp: new Date().toISOString(),
        }),
      })

      if (!kotResponse.ok) {
        const errorData = await kotResponse.json().catch(() => ({}))
        throw new Error(errorData.message || 'Failed to send KOT')
      }

      addToast("Success: KOT sent to kitchen", "success")
      
      navigate(`/print-kot/${tableId}`, {
        state: {
          currentBill: {
            _id: holdBillId,
            items: formattedItems,
            names: customerName.trim(),
            mobileNumber: customerMobile.trim(),
            discountPercentage: discountType === 'percentage' ? Number(discountPercentage) : 0,
            billAmount: total
          },
          tableNumber: tableDetails?.tableNumber || tableDetails?.tableName
        }
      })
      
    } catch (error) {
      console.error('Error sending KOT:', error)
      addToast(`Failed to send KOT: ${error.message}`, "error")
    } finally {
      setSendingKOT(false)
    }
  }

  const handleUpdateExistingItem = (updatedItem) => {
    setSelectedItems((prevItems) => {
      const itemId = updatedItem.id || updatedItem._id
      if (updatedItem.quantity <= 0) {
        return prevItems.filter((item) => {
          const currentItemId = item.id || item._id
          const isMatch =
            currentItemId === itemId &&
            (item.selectedVariant?.name === updatedItem.selectedVariant?.name ||
              (!item.selectedVariant && !updatedItem.selectedVariant))
          return !isMatch
        })
      }

      return prevItems.map((item) => {
        const currentItemId = item.id || item._id
        const isMatch =
          currentItemId === itemId &&
          (item.selectedVariant?.name === updatedItem.selectedVariant?.name ||
            (!item.selectedVariant && !updatedItem.selectedVariant))

        if (isMatch) {
          return { ...item, quantity: updatedItem.quantity }
        }
        return item
      })
    })
  }

  const handleRemoveExistingItem = (itemToRemove) => {
    setSelectedItems((prevItems) => {
      return prevItems.filter((item) => {
        const itemId = item.id || item._id
        const removeId = itemToRemove.id || itemToRemove._id || itemToRemove.productId
        const isSameProduct = itemId === removeId

        if (itemToRemove.selectedVariant && item.selectedVariant) {
          return !(isSameProduct && item.selectedVariant.name === itemToRemove.selectedVariant.name)
        } else if (!itemToRemove.selectedVariant && !item.selectedVariant) {
          return !isSameProduct
        }
        return true
      })
    })

    addToast(`Removed ${itemToRemove.item_name}`, "success")
  }

  const updateItemNote = (itemId, note) => {
    setSelectedItems(prev => prev.map(i =>
      (i.id === itemId || i._id === itemId) ? { ...i, note } : i
    ))
  }

  const handleVariantChange = (item, newVariant) => {
    setSelectedItems(prev => prev.map(i => {
      const itemId = i.id || i._id
      const targetId = item.id || item._id
      const isMatch = itemId === targetId && 
        ((i.selectedVariant?.name === item.selectedVariant?.name) || 
         (!i.selectedVariant && !item.selectedVariant))
      
      if (isMatch) {
        return {
          ...i,
          selectedVariant: newVariant,
          price: newVariant.price
        }
      }
      return i
    }))

    setEditingVariantId(null)
    addToast(`Variant changed to ${newVariant.name}`, "success")
  }

  const MenuItem = ({ product }) => {
    const quantity = getItemQuantity(product)
    const productId = product.id || product._id
    const existingItemWithVariant = selectedItems.find((item) => {
      const itemId = item.id || item._id
      return itemId === productId && item.selectedVariant
    })

    const existingItemNoVariant = selectedItems.find((item) => {
      const itemId = item.id || item._id
      return itemId === productId && !item.selectedVariant
    })

    const getPriceDisplay = () => {
      if (product.variants && product.variants.length > 0) {
        return (
          <div>
            <p className="text-xl md:text-2xl font-black text-slate-900 tracking-tighter">
              ₹{Math.min(...product.variants.map((v) => v.price))}
            </p>
            <p className="text-[11px] text-slate-400 mt-1 font-medium">
              {product.variants.length} variants
            </p>
          </div>
        )
      }
      if (product.pricingType === "mrp") {
        return (
          <p className="text-xl md:text-2xl font-black text-slate-900 tracking-tighter">
            ₹{product.sellingPrice || product.mrp}
          </p>
        )
      }
      return (
        <p className="text-xl md:text-2xl font-black text-slate-900 tracking-tighter">
          ₹{product.basePrice}
        </p>
      )
    }

const handleDirectAdd = (e) => {
  e.stopPropagation()
  if (product.variants && product.variants.length > 0) {
    if (existingItemWithVariant) {
      handleQuantityChange(existingItemWithVariant, 1)
    } else {
      setSelectedProduct(product)
      setShowVariantModal(true)
    }
  } else {
    const detectedPricingType = determinePricingType(product);
    
    const baseProduct = {
      ...product,
      price: detectedPricingType === "mrp" 
        ? product.sellingPrice || product.mrp 
        : product.basePrice,
      pricingType: detectedPricingType,
      category: product.category || 'Veg'
    }
    handleQuantityChange(baseProduct, 1)
  }
}

    const handleDirectRemove = (e) => {
      e.stopPropagation()
      if (quantity > 0) {
        if (!product.variants || product.variants.length === 0) {
          if (existingItemNoVariant) handleQuantityChange(existingItemNoVariant, -1)
        } else if (existingItemWithVariant) {
          handleQuantityChange(existingItemWithVariant, -1)
        }
      }
    }

    return (
      <div
        className={`bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden flex flex-col transition-all group ${quantity > 0 ? 'ring-2 ring-blue-500 shadow-xl shadow-blue-100/50' : 'hover:shadow-xl hover:shadow-slate-200/50 hover:-translate-y-1'} cursor-pointer`}
        onClick={() => handleItemPress(product)}
      >
        <div className="relative h-44 md:h-48 overflow-hidden bg-slate-50">
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.item_name}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Coffee size={48} className="text-slate-300" />
            </div>
          )}
          <div className="absolute top-4 left-4">
            <div
              className={`w-3 h-3 rounded-full shadow-lg ${product.category === "Veg" ? "bg-green-500" : "bg-red-500"}`}
            />
          </div>
          {quantity > 0 && (
            <div className="absolute top-4 right-4 bg-blue-600 text-white p-1.5 rounded-lg shadow-lg">
              <CheckCircle2 size={14} strokeWidth={3} />
            </div>
          )}
        </div>
        <div className="p-6 flex flex-col flex-1">
          <div className="flex-1">
            <h3 className="text-base md:text-lg font-black text-slate-800 mb-1 tracking-tight">{product.item_name}</h3>
            {existingItemWithVariant && (
              <p className="text-[11px] font-bold text-blue-600 mb-2">
                {existingItemWithVariant.selectedVariant.name}
              </p>
            )}
          </div>
          <div className="mt-4 flex items-center justify-between">
            {getPriceDisplay()}
            <button 
              onClick={handleDirectAdd}
              className="w-11 h-11 rounded-2xl flex items-center justify-center transition-all bg-blue-50 text-blue-600 hover:bg-[#0047FF] hover:text-white shadow-sm hover:shadow-blue-100 group-hover:scale-105 active:scale-90"
            >
              <Plus size={22} strokeWidth={3} />
            </button>
          </div>
        </div>
      </div>
    )
  }

  const VariantSelectionModal = () => (
    <div
      className={`fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-50 transition-opacity duration-300 ${
        showVariantModal ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      <div className="bg-white rounded-[2rem] w-11/12 max-w-md overflow-hidden shadow-xl transform transition-all">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-6">
          <h3 className="text-xl font-black text-white tracking-tight flex items-center">
            <BookOpen className="mr-2" size={20} />
            {selectedProduct?.item_name} - Select Variant
          </h3>
        </div>
        <div className="p-4 max-h-96 overflow-y-auto">
          {selectedProduct?.variants.map((variant) => (
            <div
              key={variant.name}
              className="flex justify-between items-center p-4 my-2 border border-slate-200 rounded-2xl hover:bg-blue-50 hover:border-blue-200 transition-all cursor-pointer"
              onClick={() => {
                handleQuantityChange({ 
                  ...selectedProduct, 
                  selectedVariant: variant,
                  pricingType: selectedProduct.pricingType || selectedProduct.pricing_type || 'basePrice'
                }, 1)
                setShowVariantModal(false)
              }}
            >
              <p className="text-slate-800 font-bold">{variant.name}</p>
              <p className="text-blue-600 font-black text-lg">₹{variant.price}</p>
            </div>
          ))}
        </div>
        <div className="p-4 border-t border-slate-100">
          <button
            className="w-full py-3 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-2xl transition-colors"
            onClick={() => setShowVariantModal(false)}
          >
            <X className="inline mr-2" size={18} />
            Cancel
          </button>
        </div>
      </div>
    </div>
  )

  const HoldBillsSelectionModal = () => (
    <div
      className={`fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-50 transition-opacity duration-300 ${
        showHoldBillsModal ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      <div className="bg-white rounded-[2rem] w-11/12 max-w-2xl overflow-hidden shadow-xl transform transition-all">
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-6">
          <h3 className="text-xl font-black text-white tracking-tight flex items-center">
            <Clock className="mr-2" size={20} />
            Existing Hold Bills - {tableDetails?.tableName || `Table ${tableId}`}
          </h3>
        </div>
        <div className="p-4 max-h-96 overflow-y-auto">
          {tableHoldBills.map((holdBill) => {
            const billId = holdBill.id || holdBill._id
            const displayId = billId.substring(billId.length - 6)
            return (
              <div
                key={billId}
                className="bg-white border border-slate-200 rounded-2xl p-4 mb-4 hover:border-amber-400 hover:shadow-md transition-all cursor-pointer"
                onClick={() => selectExistingHoldBill(holdBill)}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-black text-slate-800">Bill #{displayId}</p>
                    <p className="text-xs text-slate-500 flex items-center mt-1 font-medium">
                      <Clock size={12} className="mr-1" />
                      {new Date(holdBill.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-700 font-bold">{holdBill.items.length} items</p>
                    <p className="font-black text-amber-600 text-lg">₹{holdBill.totalAmount}</p>
                  </div>
                </div>

                {holdBill.names && <p className="text-sm text-blue-600 mt-2 font-medium">Name: {holdBill.names}</p>}
                <div className="mt-3 border-t border-slate-100 pt-3">
                  <p className="text-sm font-black text-slate-700 mb-2">Items:</p>
                  <div className="space-y-2">
                    {holdBill.items.slice(0, 3).map((item, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <div className="flex items-center">
                          <div
                            className={`w-2 h-2 rounded-full ${item.category === "Veg" ? "bg-green-500" : "bg-red-500"} mr-2`}
                          ></div>
                          <p className="text-sm font-medium">
                            {item.item_name}
                            {item.variant ? ` (${item.variant.name})` : ""}
                          </p>
                        </div>
                        <p className="text-sm font-black">x{item.quantity}</p>
                      </div>
                    ))}
                    {holdBill.items.length > 3 && (
                      <p className="text-xs text-slate-500 italic font-medium">+{holdBill.items.length - 3} more items</p>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex justify-end">
                  <span className="text-sm text-blue-600 font-black flex items-center">
                    Update <ChevronRight size={16} />
                  </span>
                </div>
              </div>
            )
          })}
        </div>
        <div className="p-4 border-t border-slate-100 flex gap-3">
          <button
            className="flex-1 py-3 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-2xl transition-colors"
            onClick={() => setShowHoldBillsModal(false)}
          >
            Cancel
          </button>
          <button
            className="flex-1 py-3 px-4 bg-amber-600 text-white font-bold rounded-2xl transition-all hover:bg-amber-700 shadow-lg shadow-amber-100"
            onClick={() => {
              setShowHoldBillsModal(false)
              navigate("/menu-selection", { state: { tableId, isAddingItems: false } })
            }}
          >
            Create New
          </button>
        </div>
      </div>
    </div>
  )

  const Toast = ({ message, type }) => {
    const bgColor = type === "success" ? "bg-green-500" : type === "error" ? "bg-red-500" : "bg-blue-500"
    const Icon = type === "success" ? Check : type === "error" ? AlertTriangle : Info
    return (
      <div className={`${bgColor} text-white px-6 py-4 rounded-2xl shadow-lg flex items-center mb-3 animate-fade-in`}>
        <Icon className="mr-3" size={20} />
        <p className="font-bold">{message}</p>
      </div>
    )
  }

  const CartSection = () => (
    <div className="flex flex-col h-full bg-white">
   <div className="p-4 md:p-6 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-indigo-50">
  <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-3">Order Type</p>
  <div className="grid grid-cols-2 gap-2">
    <button
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        setOrderType('dining')
      }}
      className={`flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm transition-all ${
        orderType === 'dining'
          ? 'bg-blue-600 text-white shadow-lg shadow-blue-100'
          : 'bg-white text-slate-600 border border-slate-200 hover:border-blue-300'
      }`}
    >
      <Utensils size={18} />
      <span>Dining</span>
    </button>
    <button
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        setOrderType('takeaway')
      }}
      className={`flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm transition-all ${
        orderType === 'takeaway'
          ? 'bg-orange-500 text-white shadow-lg shadow-orange-100'
          : 'bg-white text-slate-600 border border-slate-200 hover:border-orange-300'
      }`}
    >
      <ShoppingCart size={18} />
      <span>Takeaway</span>
    </button>
  </div>
</div>
      <div className="p-4 md:p-6 border-b border-slate-100 bg-slate-50/30">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2 text-blue-600">
            <span className="text-[10px] font-black uppercase tracking-[0.2em]">Table Order</span>
            <CheckCircle2 size={12} />
          </div>
          <button className="lg:hidden text-slate-400" onClick={() => setIsCartMobileOpen(false)}>
            <X size={20} />
          </button>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {loadingTable ? (
              <div className="flex items-center gap-2">
                <Loader2 size={16} className="animate-spin text-slate-400" />
                <h2 className="text-xl md:text-2xl font-black text-slate-900 tracking-tighter">Loading...</h2>
              </div>
            ) : tableDetails ? (
              <div>
                <h2 className="text-xl md:text-2xl font-black text-slate-900 tracking-tighter">
                  {tableDetails.tableName}
                </h2>
                {tableDetails.tableNumber && (
                  <p className="text-xs font-bold text-slate-400">#{tableDetails.tableNumber}</p>
                )}
              </div>
            ) : (
              <h2 className="text-xl md:text-2xl font-black text-slate-900 tracking-tighter">
                Table {String(tableId).substring(0, 6)}
              </h2>
            )}
          </div>
          {isAddingItems && (
            <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-lg text-xs font-black">UPDATING</span>
          )}
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6 scrollbar-hide">
        {selectedItems.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-8 opacity-40">
            <div className="w-20 h-20 bg-slate-50 text-slate-200 rounded-full flex items-center justify-center mb-6 ring-8 ring-slate-50/50">
              <ShoppingBag size={32} />
            </div>
            <p className="text-sm font-black text-slate-400">Cart is Empty</p>
            <p className="text-[11px] font-medium text-slate-300 mt-2">Select items from menu to build order</p>
          </div>
        ) : (
          <>
            {selectedItems.map((item, idx) => {
              const itemKey = `${item.id || item._id}-${item.selectedVariant?.name || "base"}-${idx}`
              return (
                <div key={itemKey} className="animate-in fade-in slide-in-from-right-4 duration-300">
                  <div className="flex items-start gap-4">
                    <div className="flex flex-col items-center gap-1 bg-slate-100/50 rounded-xl p-1 shrink-0">
                      <button 
                        onClick={() => handleUpdateExistingItem({ ...item, quantity: (item.quantity || 1) + 1 })} 
                        className="p-1.5 hover:bg-white rounded-lg text-slate-400 hover:text-blue-600 transition-colors"
                      >
                        <Plus size={14} strokeWidth={3} />
                      </button>
                      <span className="text-sm font-black text-slate-800">{item.quantity}</span>
                      <button 
                        onClick={() => handleUpdateExistingItem({ ...item, quantity: Math.max(0, (item.quantity || 1) - 1) })} 
                        className="p-1.5 hover:bg-white rounded-lg text-slate-400 hover:text-rose-500 transition-colors"
                      >
                        <Minus size={14} strokeWidth={3} />
                      </button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start mb-0.5">
                        <div className="flex-1">
                          <h4 className="text-sm font-black text-slate-800">{item.item_name}</h4>
                          {item.selectedVariant && (
                            <div className="relative">
                              <button
                                onClick={() => {
                                  const product = products.find(p => 
                                    (p.id === item.id || p._id === item._id)
                                  )
                                  if (product?.variants?.length > 1) {
                                    const itemKey = `${item.id || item._id}-${item.selectedVariant?.name || "base"}-${idx}`
                                    setEditingVariantId(editingVariantId === itemKey ? null : itemKey)
                                  }
                                }}
                                className="text-[10px] font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1 mt-0.5 group"
                              >
                                {item.selectedVariant.name}
                                {(() => {
                                  const product = products.find(p => 
                                    (p.id === item.id || p._id === item._id)
                                  )
                                  return product?.variants?.length > 1 && (
                                    <ChevronRight size={10} className={`transition-transform ${editingVariantId === `${item.id || item._id}-${item.selectedVariant?.name || "base"}-${idx}` ? 'rotate-90' : ''}`} />
                                  )
                                })()}
                              </button>
                              
                              {(() => {
                                const itemKey = `${item.id || item._id}-${item.selectedVariant?.name || "base"}-${idx}`
                                const product = products.find(p => 
                                  (p.id === item.id || p._id === item._id)
                                )
                                
                                return editingVariantId === itemKey && product?.variants && (
                                  <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10 min-w-[160px] animate-in fade-in slide-in-from-top-2 duration-200">
                                    <div className="p-1 max-h-48 overflow-y-auto">
                                      {product.variants.map((variant) => (
                                        <button
                                          key={variant.name}
                                          onClick={() => handleVariantChange(item, variant)}
                                          className={`w-full text-left px-3 py-2 rounded-md text-xs font-bold transition-colors ${
                                            variant.name === item.selectedVariant?.name
                                              ? 'bg-blue-50 text-blue-700'
                                              : 'text-slate-700 hover:bg-slate-50'
                                          }`}
                                        >
                                          <div className="flex justify-between items-center">
                                            <span>{variant.name}</span>
                                            <span className="text-blue-600">₹{variant.price}</span>
                                          </div>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )
                              })()}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => handleRemoveExistingItem(item)}
                          className="ml-2 p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                        >
                          <Trash2 size={14} />
                        </button>
                        <span className="text-sm font-black text-slate-900 ml-2">₹{((item.selectedVariant ? item.selectedVariant.price : item.price) * (item.quantity || 1)).toFixed(2)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-[11px] font-bold text-slate-400">₹{(item.selectedVariant ? item.selectedVariant.price : item.price).toFixed(2)}/u</p>
                        <button 
                          onClick={() => setEditingNoteId(editingNoteId === item.id ? null : item.id)}
                          className={`text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 ${item.note ? 'text-blue-600' : 'text-slate-300 hover:text-slate-500'}`}
                        >
                          <StickyNote size={12} />
                          {item.note ? 'Edit' : 'Note'}
                        </button>
                      </div>
                      
                      {(editingNoteId === item.id || item.note) && (
                        <div className="mt-2 relative animate-in slide-in-from-top-2 duration-200">
                          <input 
                            type="text"
                            placeholder="e.g. Extra spicy, no onions..."
                            value={item.note}
                            onChange={(e) => updateItemNote(item.id, e.target.value)}
                            className="w-full bg-slate-50 border-none rounded-lg py-2 px-3 text-[11px] font-bold placeholder:text-slate-300 focus:ring-1 focus:ring-blue-100"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
            
            <div className="border-t border-slate-100 pt-4 space-y-3">
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setShowCustomerDetails(!showCustomerDetails)
                }}
                className="w-full flex items-center justify-between py-2.5 px-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-all"
              >
                <div className="flex items-center gap-2">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Customer Details</p>
                  {(customerName || customerMobile) && (
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  )}
                </div>
                <ChevronRight 
                  size={16} 
                  className={`text-slate-400 transition-transform ${showCustomerDetails ? 'rotate-90' : ''}`}
                />
              </button>
              
              {showCustomerDetails && (
                <div 
                  className="space-y-3 animate-in slide-in-from-top-2 duration-200"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="text"
                    placeholder="Customer Name (Optional)"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className="w-full bg-slate-50 border-none rounded-lg py-2.5 px-3 text-sm font-medium placeholder:text-slate-400 focus:ring-2 focus:ring-blue-100"
                  />
                  
                  <input
                    type="tel"
                    placeholder="Mobile Number (Optional)"
                    value={customerMobile}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '')
                      if (value.length <= 10) setCustomerMobile(value)
                    }}
                    maxLength="10"
                    className={`w-full bg-slate-50 border-none rounded-lg py-2.5 px-3 text-sm font-medium placeholder:text-slate-400 focus:ring-2 ${
                      customerMobile && !/^[6-9]\d{9}$/.test(customerMobile) 
                        ? 'ring-2 ring-red-200 focus:ring-red-300' 
                        : 'focus:ring-blue-100'
                    }`}
                  />
                  {customerMobile && !/^[6-9]\d{9}$/.test(customerMobile) && customerMobile.length === 10 && (
                    <p className="text-[10px] text-red-500 font-bold">Invalid mobile number</p>
                  )}
                </div>
              )}
            </div>

            <div className="border-t border-slate-100 pt-4 space-y-3">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Payment Mode</p>

              <div className="grid grid-cols-4 gap-2">
                {[
                  { mode: 'Cash', icon: <DollarSign size={8} /> },   
                  { mode: 'UPI', icon: <Smartphone size={8} /> },    
                  { mode: 'Card', icon: <CreditCard size={8} /> },   
                  { mode: 'Other', icon: <FileText size={8} /> }     
                ].map(({ mode, icon }) => (
                  <button
                    key={mode}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setPaymentMode(mode)
                    }}
                    className={`flex flex-col items-center justify-center gap-1 py-2 rounded-lg font-bold text-[10px] transition-all ${
                      paymentMode === mode 
                        ? 'bg-slate-900 text-white shadow-md' 
                        : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                    }`}
                  >
                    {icon}
                    {mode}
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t border-slate-100 pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Applied Discount</p>
                {discount > 0 && (
                  <span className="text-sm font-black text-emerald-500">-₹{discount.toFixed(2)}</span>
                )}
              </div>
              
              <div className="grid grid-cols-4 gap-2">
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setDiscountType('percentage')
                    setDiscountPercentage(0)
                    setDiscountAmount(0)
                  }}
                  className={`py-2.5 rounded-xl font-bold text-xs transition-all ${
                    discountPercentage === 0 && discountAmount === 0
                      ? 'bg-slate-900 text-white shadow-md'
                      : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                  }`}
                >
                  None
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setDiscountType('percentage')
                    setDiscountPercentage(5)
                    setDiscountAmount(0)
                  }}
                  className={`py-2.5 rounded-xl font-bold text-xs transition-all ${
                    discountPercentage === 5 && discountType === 'percentage'
                      ? 'bg-slate-900 text-white shadow-md'
                      : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                  }`}
                >
                  5%
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setDiscountType('percentage')
                    setDiscountPercentage(10)
                    setDiscountAmount(0)
                  }}
                  className={`py-2.5 rounded-xl font-bold text-xs transition-all ${
                    discountPercentage === 10 && discountType === 'percentage'
                      ? 'bg-slate-900 text-white shadow-md'
                      : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                  }`}
                >
                  10%
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setDiscountType('percentage')
                    setDiscountPercentage(15)
                    setDiscountAmount(0)
                  }}
                  className={`py-2.5 rounded-xl font-bold text-xs transition-all ${
                    discountPercentage === 15 && discountType === 'percentage'
                      ? 'bg-slate-900 text-white shadow-md'
                      : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                  }`}
                >
                  15%
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-2 mt-3">
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setDiscountType('percentage')
                  }}
                  className={`flex items-center justify-center gap-1.5 py-2 rounded-lg font-bold text-xs transition-all ${
                    discountType === 'percentage'
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                  }`}
                >
                  <Percent size={14} />
                  Percentage
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setDiscountType('amount')
                  }}
                  className={`flex items-center justify-center gap-1.5 py-2 rounded-lg font-bold text-xs transition-all ${
                    discountType === 'amount'
                      ? 'bg-green-600 text-white shadow-md'
                      : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                  }`}
                >
                  <Tag size={14} />
                  Amount
                </button>
              </div>
              
              <div onClick={(e) => e.stopPropagation()}>
                {discountType === 'percentage' ? (
                  <input
                    type="number"
                    placeholder="Enter % (0-100)"
                    value={discountPercentage || ''}
                    onChange={(e) => {
                      const num = Number(e.target.value)
                      if (!isNaN(num) && num >= 0 && num <= 100) {
                        setDiscountPercentage(num)
                      }
                    }}
                    min="0"
                    max="100"
                    className="w-full bg-slate-50 border-none rounded-lg py-2.5 px-3 text-sm font-medium placeholder:text-slate-400 focus:ring-2 focus:ring-blue-100"
                  />
                ) : (
                  <input
                    type="number"
                    placeholder={`Max ₹${subtotal.toFixed(2)}`}
                    value={discountAmount || ''}
                    onChange={(e) => {
                      const num = Number(e.target.value)
                      if (!isNaN(num) && num >= 0 && num <= subtotal) {
                        setDiscountAmount(num)
                      }
                    }}
                    min="0"
                    max={subtotal}
                    className="w-full bg-slate-50 border-none rounded-lg py-2.5 px-3 text-sm font-medium placeholder:text-slate-400 focus:ring-2 focus:ring-green-100"
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="bg-slate-50 border-t border-slate-100 p-6 space-y-6">
        <div className="space-y-2">
          {(() => {
            const { basePriceSubtotal, mrpSubtotal, subtotal, cgst, sgst, discount, total } = calculateTotals();
            
            return (
              <>
                {basePriceSubtotal > 0 && (
                  <div className="flex justify-between text-sm font-bold text-slate-700">
                    <span>Base Price Items</span>
                    <span>₹{basePriceSubtotal.toFixed(2)}</span>
                  </div>
                )}
                {mrpSubtotal > 0 && (
                  <div className="flex justify-between text-sm font-bold text-slate-700">
                    <span>MRP Items (No GST)</span>
                    <span>₹{mrpSubtotal.toFixed(2)}</span>
                  </div>
                )}
                
                <div className="flex justify-between text-sm font-bold text-slate-700 pt-2 border-t border-slate-200">
                  <span>Subtotal</span>
                  <span>₹{subtotal.toFixed(2)}</span>
                </div>
                
                {basePriceSubtotal > 0 && (
                  <>
                    <div className="flex justify-between text-sm font-bold text-slate-600">
                      <span>CGST (2.5%)</span>
                      <span>₹{cgst.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-bold text-slate-600">
                      <span>SGST (2.5%)</span>
                      <span>₹{sgst.toFixed(2)}</span>
                    </div>
                  </>
                )}
                
                {discount > 0 && (
                  <div className="flex justify-between text-sm font-bold text-emerald-600">
                    <span>Discount</span>
                    <span>-₹{discount.toFixed(2)}</span>
                  </div>
                )}
                
                <div className="flex justify-between items-end pt-3 border-t border-slate-200">
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">Payable Total</p>
                    <h3 className="text-3xl font-black text-slate-900 tracking-tighter">
                      ₹{total.toFixed(2)}
                    </h3>
                    {basePriceSubtotal > 0 && (
                      <p className="text-[10px] font-bold text-emerald-500 mt-1">INCL GST</p>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button 
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              if (isAddingItems && existingHoldBillId) {
                handleProceed()
              } else {
                handleDirectHold()
              }
            }}
            disabled={loading}
            className="flex items-center justify-center gap-2 bg-white border border-slate-200 text-slate-700 py-3.5 rounded-2xl font-bold text-sm hover:bg-slate-50 transition-all disabled:opacity-50"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
            Hold Bill
          </button>
          
          <button 
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handleSendKOT()
            }}
            disabled={sendingKOT}
            className="flex items-center justify-center gap-2 bg-white border border-slate-200 text-indigo-600 py-3.5 rounded-2xl font-bold text-sm hover:bg-indigo-50 transition-all disabled:opacity-50"
          >
            {sendingKOT ? <Loader2 size={18} className="animate-spin" /> : <ChefHat size={18} />}
            Send KOT
          </button>
        </div>

        <button 
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            handleSaveAndPrintBill()
          }}
          disabled={savingBill}
          className="w-full flex items-center justify-center gap-3 bg-[#0047FF] text-white py-4 rounded-2xl font-bold text-sm shadow-xl shadow-blue-100 hover:bg-blue-700 transition-all transform active:scale-95 disabled:opacity-50"
        >
          {savingBill ? <Loader2 size={18} className="animate-spin" strokeWidth={3} /> : <Printer size={18} strokeWidth={3} />}
          Finalize & Print
        </button>
      </div>
    </div>
  )

  const productTypes = ["all", ...new Set(products.map((product) => product.type || "Other"))]

  return (
    <div className="flex flex-col lg:flex-row h-screen bg-[#F8FAFC]">
      <div className="flex-1 overflow-y-auto p-4 md:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-10">
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tighter">Menu Selection</h1>
            <div className="flex items-center gap-3 mt-1">
              {tableId && (
                <>
                  <div className="flex items-center gap-1.5 text-xs font-bold text-slate-400">
                    <span>Table {tableId}</span>
                  </div>
                  <span className="w-1 h-1 bg-slate-200 rounded-full"></span>
                </>
              )}
              <p className="text-xs font-bold text-slate-400">{new Date().toLocaleDateString()}</p>
            </div>
          </div>
          <div className="relative w-full sm:w-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search dishes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-2xl text-sm w-full sm:w-80 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm transition-all"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 mb-10 overflow-x-auto pb-4 scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
          {productTypes.map(type => (
            <button
              key={type}
              onClick={() => setActiveTab(type)}
              className={`px-6 py-2.5 rounded-2xl text-xs md:text-sm font-black whitespace-nowrap transition-all ${
                activeTab === type 
                ? 'bg-slate-900 text-white shadow-xl scale-105' 
                : 'bg-white text-slate-500 border border-slate-100 hover:border-slate-300 shadow-sm'
              }`}
            >
              {type === "all" ? "All Items" : type}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-40">
            <Loader2 className="animate-spin text-blue-600" size={40} />
          </div>
        ) : Object.entries(groupedProducts).length > 0 ? (
          Object.entries(groupedProducts).map(([type, typeProducts]) => (
            <div key={type} className="mb-6">
              <h2 className="text-xl font-black text-slate-800 mb-4 flex items-center tracking-tight">
                <Coffee className="mr-2" size={20} />
                {type}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {typeProducts.map((product) => (
                  <MenuItem key={product.id || product._id} product={product} />
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center h-40 bg-white rounded-2xl shadow p-6">
            <AlertTriangle size={40} className="text-amber-500 mb-4" />
            <p className="text-slate-700 text-lg font-black mb-2">No items found</p>
            <p className="text-slate-500 text-center font-medium">
              {searchQuery ? `No results for "${searchQuery}"` : "No menu items available"}
            </p>
          </div>
        )}
      </div>

      <div className="hidden lg:block w-[380px] xl:w-[480px] border-l border-slate-100 bg-white">
        <CartSection />
      </div>

      {selectedItems.length > 0 && !isCartMobileOpen && (
        <button 
          onClick={() => setIsCartMobileOpen(true)}
          className="lg:hidden fixed bottom-6 right-6 left-6 h-16 bg-[#0047FF] text-white rounded-[2rem] shadow-2xl flex items-center justify-between px-8 z-30 animate-in slide-in-from-bottom-10"
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <ShoppingBag size={24} />
              <span className="absolute -top-2 -right-2 w-5 h-5 bg-white text-blue-600 rounded-full text-[10px] font-black flex items-center justify-center shadow-md">
                {selectedItems.length}
              </span>
            </div>
            <span className="font-black text-sm uppercase tracking-widest">Order Summary</span>
          </div>
          <div className="flex flex-col items-end">
            <span className="font-black text-lg">₹{totalAmount.toFixed(2)}</span>
          </div>
        </button>
      )}

      <div 
        className={`lg:hidden fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-40 transition-opacity duration-300 ${isCartMobileOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
        onClick={() => setIsCartMobileOpen(false)}
      />
      <div className={`lg:hidden fixed bottom-0 left-0 right-0 h-[90vh] bg-white rounded-t-[3rem] z-50 transform transition-transform duration-500 shadow-2xl overflow-hidden ${isCartMobileOpen ? 'translate-y-0' : 'translate-y-full'}`}>
        <CartSection />
      </div>

      <div className="fixed top-20 right-4 z-50">
        {toasts.map((toast) => (
          <Toast key={toast.id} message={toast.message} type={toast.type} />
        ))}
      </div>

      <VariantSelectionModal />
      <HoldBillsSelectionModal />
      <CustomAlertModal />
    </div>
  )
}

export default MenuSelection;